import type { CSSProperties, FormEvent } from 'react';
import { useState } from 'react';

import BpkButton, {
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkFieldset from '@skyscanner-internal/backpack-web/bpk-component-fieldset';
import BpkInput, {
  INPUT_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-input';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text/src/BpkText';

import { useMBDContext } from '../common/MBD/useMBDContext';

import {
  getValidationError,
  navigateToFlightTracker,
  normalizeFlightNumber,
} from './helpers';

import type { FlightTrackerSearchControlsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightTrackerSearchControlsProps';

import STYLES from './FlightTrackerSearchControls.module.scss';

const FlightTrackerSearchControls = ({
  backgroundImageUrl,
  isMainTitle = true,
  strings,
  title,
}: FlightTrackerSearchControlsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const inputMbdComponentName = `${rawComponentName}_input`;
  const ctaMbdComponentName = `${rawComponentName}_cta`;
  const [flightNumber, setFlightNumber] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const validateFlightNumber = (value: string) => {
    const error = getValidationError(value, strings);
    setValidationError(error);
    return error;
  };

  const getLiveValidationError = (value: string) => {
    if (value.length < 3) {
      return null;
    }

    return getValidationError(value, strings);
  };

  const submitFlightNumber = () => {
    const normalizedFlightNumber = normalizeFlightNumber(flightNumber);
    const error = validateFlightNumber(normalizedFlightNumber);

    setFlightNumber(normalizedFlightNumber);

    if (error) {
      return;
    }

    navigateToFlightTracker(normalizedFlightNumber);
  };

  const handleChange = (value: string) => {
    const normalizedValue = normalizeFlightNumber(value);

    setFlightNumber(normalizedValue);
    setIsDirty(true);
    setValidationError(getLiveValidationError(normalizedValue));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitFlightNumber();
  };

  return (
    <div
      className={STYLES.FlightTrackerSearchControls}
      style={
        {
          '--background-image': `url(${backgroundImageUrl})`,
        } as CSSProperties
      }
      data-testid="flight-tracker-search-controls"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.FlightTrackerSearchControls__content}>
        <div className={STYLES.FlightTrackerSearchControls__panelWrapper}>
          <form
            className={STYLES.FlightTrackerSearchControls__panel}
            noValidate
            onSubmit={handleSubmit}
          >
            <div className={STYLES.FlightTrackerSearchControls__titleWrapper}>
              <BpkText
                tagName={isMainTitle ? 'h1' : 'h2'}
                textStyle={TEXT_STYLES.hero6}
              >
                {title}
              </BpkText>
            </div>

            <BpkText>{strings.FlightTracker_subheading}</BpkText>

            <div className={STYLES.FlightTrackerSearchControls__inputWrapper}>
              <BpkFieldset
                label={strings.FlightTracker_form_flight_number_label}
                required
                validationMessage={validationError}
              >
                <BpkInput
                  id="flight-number-input"
                  type={INPUT_TYPES.text}
                  name="flightNumber"
                  placeholder={
                    strings.FlightTracker_form_flight_number_placeholder
                  }
                  value={flightNumber}
                  large
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={() => {
                    if (isDirty) {
                      validateFlightNumber(flightNumber);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      submitFlightNumber();
                    }
                  }}
                  pattern="(?=[A-Z0-9]{2,3}[0-9]{1,4}$)(?=.*[A-Z])[A-Z0-9]{2,3}[0-9]{1,4}"
                  autoFocus
                  valid={validationError ? false : undefined}
                  aria-invalid={validationError ? true : undefined}
                  data-tracking-element-id={inputMbdComponentName}
                  data-tracking-common-params={buildMBDParam(
                    inputMbdComponentName,
                  )}
                />
              </BpkFieldset>
            </div>

            <div className={STYLES.FlightTrackerSearchControls__buttonWrapper}>
              <BpkButton
                fullWidth
                size={SIZE_TYPES.large}
                onClick={submitFlightNumber}
                data-tracking-element-id={ctaMbdComponentName}
                data-tracking-common-params={buildMBDParam(ctaMbdComponentName)}
              >
                {strings.FlightTracker_form_cta}
              </BpkButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FlightTrackerSearchControls;
